import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { useSubscription } from '../hooks/useSubscription';
import * as routes from '../utils/routes';
import { AxiosError } from 'axios';

const SubscriptionCallback: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
	const [message, setMessage] = useState('Processing your payment...');
	const { refetchSubscription } = useSubscription();
	const [isVerifying, setIsVerifying] = useState(false);
	const hasRun = useRef(false);

	useEffect(() => {
		// Detect redirect loop
		const redirectCount = sessionStorage.getItem('subscriptionCallbackCount');
		const lastCallbackTime = sessionStorage.getItem('lastCallbackTime');
		const currentTime = Date.now();

		// Reset counter if more than 5 minutes have passed
		if (lastCallbackTime && currentTime - parseInt(lastCallbackTime) > 300000) {
			sessionStorage.removeItem('subscriptionCallbackCount');
			sessionStorage.removeItem('lastCallbackTime');
		}

		const count = redirectCount ? parseInt(redirectCount) : 0;

		// If we've been redirected here more than 3 times in 5 minutes, break the loop
		if (count >= 3) {
			setStatus('error');
			setMessage('Too many redirects detected. Please try again later or contact support.');
			sessionStorage.removeItem('subscriptionCallbackCount');
			sessionStorage.removeItem('lastCallbackTime');
			return;
		}

		// Update redirect counter
		sessionStorage.setItem('subscriptionCallbackCount', (count + 1).toString());
		sessionStorage.setItem('lastCallbackTime', currentTime.toString());

		// Prevent duplicate calls
		if (!isVerifying && !hasRun.current) {
			hasRun.current = true;
			handlePaymentCallback();
		}
	}, []);

	const handlePaymentCallback = async () => {
		// Check if already verifying
		if (isVerifying) {
			console.log('Already verifying payment, skipping duplicate call');
			return;
		}

		try {
			setIsVerifying(true);

			const reference = searchParams.get('reference');
			const trxref = searchParams.get('trxref');
			const paymentRef = reference || trxref;

			// Debug: Log all URL parameters
			console.log('All URL parameters:', Object.fromEntries(searchParams.entries()));
			console.log('Reference from URL:', reference);
			console.log('Trxref from URL:', trxref);

			if (!paymentRef) {
				setStatus('error');
				setMessage('Invalid payment reference');
				setIsVerifying(false);
				return;
			}

			// Check if this payment has already been verified in this session
			const verifiedPayments = sessionStorage.getItem('verifiedPayments');
			const verifiedList = verifiedPayments ? JSON.parse(verifiedPayments) : [];

			if (verifiedList.includes(paymentRef)) {
				console.log('Payment already verified in this session:', paymentRef);
				setStatus('success');
				setMessage('Payment already verified. Redirecting...');
				setTimeout(() => {
					window.location.href = routes.DASHBOARD;
				}, 2000);
				return;
			}

			// Verify the transaction with backend
			const response = await apiClient.post('/subscriptions/verify-payment', {
				reference: reference || trxref,
			});

			// The apiClient returns data directly, not wrapped in response object
			// Handle both standard axios response and direct data response
			let responseData, success;

			// Check for the response structure you provided
			if (response && response.status && response.status.toLowerCase() === 'success') {
				// Response has top-level status field
				success = true;
				responseData = response.data || response;
			} else if (response && response.data) {
				// Standard axios response structure
				responseData = response.data;
				success = response.status === 200 && responseData?.success === true;
			} else if (response && response.success !== undefined) {
				// apiClient returns data directly
				responseData = response;
				success = responseData.success === true;
			} else {
				// Fallback - treat any response as success if no error was thrown
				responseData = response || {};
				success = true;
			}

			if (success) {
				// Store the verified payment reference
				const verifiedPayments = sessionStorage.getItem('verifiedPayments');
				const verifiedList = verifiedPayments ? JSON.parse(verifiedPayments) : [];
				verifiedList.push(paymentRef);
				sessionStorage.setItem('verifiedPayments', JSON.stringify(verifiedList));

				// Clear redirect counter on success
				sessionStorage.removeItem('subscriptionCallbackCount');
				sessionStorage.removeItem('lastCallbackTime');

				setStatus('success');
				setMessage('Subscription activated successfully! Redirecting...');
				notify('success', 'Subscription activated successfully!');

				// Wait a bit longer for backend to fully process the subscription update
				setTimeout(async () => {
					// Refresh subscription status
					if (refetchSubscription) {
						await refetchSubscription();
					}

					// Refresh the page to ensure all state is properly updated
					window.location.href = routes.DASHBOARD;
				}, 3000);
			} else {
				setStatus('error');
				setMessage(responseData?.message || response.data?.message || 'Payment verification failed');
			}
		} catch (error) {
			console.error('Payment verification error:', error);

			// Handle specific error cases
			if (error instanceof AxiosError && error.response?.status === 409) {
				// Duplicate entry error
				setStatus('success');
				setMessage('Your subscription is already active. Redirecting...');
				notify('info', 'Subscription already processed');

				// Clear redirect counter
				sessionStorage.removeItem('subscriptionCallbackCount');
				sessionStorage.removeItem('lastCallbackTime');

				setTimeout(() => {
					window.location.href = routes.DASHBOARD;
				}, 2000);
			} else if (error instanceof AxiosError && error.response?.status === 404) {
				// Payment not found
				setStatus('error');
				setMessage('Payment reference not found. Please contact support if you were charged.');
			} else if (
				error instanceof AxiosError &&
				error.response?.status === 400 &&
				error.response?.data?.code === 'ALREADY_PROCESSED'
			) {
				// Payment already processed
				setStatus('success');
				setMessage('Payment already processed. Redirecting to dashboard...');

				// Clear redirect counter
				sessionStorage.removeItem('subscriptionCallbackCount');
				sessionStorage.removeItem('lastCallbackTime');

				setTimeout(() => {
					window.location.href = routes.DASHBOARD;
				}, 2000);
			} else {
				// Generic error
				setStatus('error');
				if (error instanceof AxiosError) {
					setMessage(error.response?.data?.message || 'Failed to verify payment. Please contact support.');
				} else {
					setMessage('Failed to verify payment. Please contact support.');
				}
				notify('error', 'Failed to verify payment');
			}
		} finally {
			setIsVerifying(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
			<div className="bg-white rounded-lg shadow-lg p-12 w-96 h-64 flex flex-col items-center justify-start pt-12">
				<div className="flex flex-col items-center">
					{status.toLowerCase() === 'processing' && (
						<>
							<CircularProgress size={48} style={{ color: '#3B82F6' }} />
							<p className="mt-6 text-lg text-gray-700 text-center px-4">{message}</p>
						</>
					)}

					{status.toLowerCase() === 'success' && (
						<>
							<CheckCircleIcon style={{ fontSize: '48px', color: '#10B981' }} />
							<p className="mt-6 text-lg text-gray-700 text-center px-4">{message}</p>
						</>
					)}

					{status.toLowerCase() === 'error' && (
						<>
							<ErrorIcon style={{ fontSize: '48px', color: '#EF4444' }} />
							<p className="mt-6 text-lg text-gray-700 text-center px-4">{message}</p>
							{/* Add retry button for certain errors */}
							{!message.includes('Too many redirects') && (
								<button
									onClick={() => {
										setStatus('processing');
										setMessage('Processing your payment...');
										handlePaymentCallback();
									}}
									className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
								>
									Retry Verification
								</button>
							)}
						</>
					)}
				</div>
			</div>

			<Link to="/subscription/manage" className="mt-6 text-blue-600 hover:text-blue-800 underline">
				Back to subscriptions
			</Link>
		</div>
	);
};

export default SubscriptionCallback;
